/**
 * edits.js — operator overrides that survive a redraft.
 *
 * The Builder DERIVES the document on every input change and never stores it,
 * which is deliberate: a stored draft sitting beside a changed file list is the
 * quiet mismatch the tool exists to remove. But it means anything an operator
 * types has to live outside the document and be re-applied afterwards.
 *
 * That makes the KEY the whole design problem. `page.id` is positional — `p1`,
 * `p2`, … handed out in draft order — so keying edits by it means adding one
 * mesh renumbers every page and silently moves every caption and arrow onto the
 * wrong page. That exact failure already shipped once here with mesh ids
 * (`m001` regenerated per rebuild while `picks` persisted, so a report was
 * built from the mesh the operator had REJECTED). Not again:
 *
 *   · page-organised libraries key on `sourcePage` — the PageNN number, which
 *     comes from the filenames and cannot drift.
 *   · template libraries key on the recipe id (`preop`, `comparison`, …), which
 *     is authored in TEMPLATES and equally stable.
 *   · pages added in PAGE MODE key on their instance id (`pageinst:i7`), minted
 *     when the operator added the page and never reused — because there the
 *     same recipe can appear twice, and the recipe id no longer names one page.
 *
 * Both are content-derived. `editKeyFor()` is the single place that decides, and
 * a page with no stable key returns null and is simply not editable, rather than
 * being given a positional key that would appear to work.
 */

import { newAnnotation } from './schema.js'

/** Caption slots an operator may override, matching `emptyCaptions()`. */
export const CAPTION_SLOTS = ['tl', 'tc', 'tr', 'bl', 'bc', 'br']

/** Annotation kinds the writer can actually draw — see layout.js annotationOps. */
export const ANNOTATION_KINDS = ['text', 'arrow', 'dimension', 'leader', 'line', 'circle']

/**
 * The stable identity of a page across redrafts, or null if it has none.
 *
 * Returns null rather than falling back to `page.id`, because a positional
 * fallback is worse than no editing: it silently reattaches an operator's work
 * to a different page.
 */
export function editKeyFor(page) {
  if (!page) return null
  if (page.pageInstanceId != null) {
    // A page the operator ADDED, in page mode. Its instance id was minted when
    // it was added and is never reused or renumbered, so an edit follows the
    // page it was made on through any reorder. The recipe id cannot serve: two
    // instances of one type share it, and keying on it would put one caption on
    // both and let "Omit this page" drop both.
    //
    // One instance can still emit several sheets — a print-models page with
    // more models than fit becomes `print-models` then `print-models#1` — so
    // that suffix is kept, or the continuation would take the first sheet's
    // edits. The `-cN` of a sheet split during pagination is kept for the same
    // reason, exactly as the sourcePage branch below does.
    const sheet = /#(\d+)$/.exec(page.recipeId ?? '')
    const part = /-c(\d+)$/.exec(page.id ?? '')
    return `pageinst:${page.pageInstanceId}${sheet ? `#${sheet[1]}` : ''}${part ? `-c${part[1]}` : ''}`
  }
  if (page.sourcePage != null) {
    // A page that split during pagination carries `-c1`, `-c2` … on its id; keep
    // that suffix so the two halves stay distinguishable.
    const part = /-c(\d+)$/.exec(page.id ?? '')
    return part ? `page:${page.sourcePage}#${part[1]}` : `page:${page.sourcePage}`
  }
  if (page.recipeId) return `recipe:${page.recipeId}`
  return null
}

/** A blank edit record. */
/** The per-page text FIELDS an operator can type: the source-data case summary, the measurement notes. */
export const PAGE_FIELDS = ['summary', 'notes']

export const newEdit = () => ({ captions: {}, annotations: [], steps: {}, fields: {}, hidden: false })

/**
 * The panels on a page that an annotation can be attached to.
 *
 * Lives here rather than in the editor because it is pure, and because getting
 * it wrong took the whole panel down: `page.rows` is an ARRAY on a measure page
 * but the row COUNT — a number — on a grid page, and grid is 13 of the 24 pages
 * in the reference report. Iterating it blindly threw
 * "(l ?? []).forEach is not a function" and React unmounted the tool.
 *
 * `viewsForScenes` in renderScenes.js already carries `Array.isArray` guards for
 * this exact field. The schema reuses one name for two shapes; every reader has
 * to check, so the check belongs somewhere tested.
 */
export function annotationTargets(page) {
  if (!page) return []
  const out = [{ value: '', label: 'Whole page' }]
  const add = (kind, list, name) => {
    if (!Array.isArray(list)) return          // grid's `rows` is a COUNT, not a list
    list.forEach((p, i) => out.push({
      value: `${kind}:${i}`,
      label: `${name} ${i + 1}${p?.view ? ` (${p.view})` : ''}`,
    }))
  }
  add('cell', page.cells, 'Panel')
  add('step', page.steps, 'Step')
  add('row', page.rows, 'Row')
  add('item', page.items, 'Item')
  return out
}

/**
 * Apply operator edits to a freshly drafted document.
 *
 * PURE — returns a new document and never mutates the input, so the Builder can
 * call it inside a `useMemo` without the draft and the edited copy drifting.
 *
 * @param {object} doc     a drafted report
 * @param {object} edits   { [editKey]: { captions, annotations, hidden } }
 * @returns {{doc: object, applied: string[], orphans: string[]}}
 *   `applied` are the keys that matched a page; `orphans` are keys that did not.
 *   Orphans are REPORTED, never dropped silently — an operator whose caption
 *   vanished because the folder was renamed needs to be told, not left guessing.
 */
export function applyEdits(doc, edits) {
  if (!doc) throw new TypeError('applyEdits: doc is required')
  if (!edits || !Object.keys(edits).length) {
    return { doc, applied: [], orphans: [] }
  }

  const applied = new Set()
  const pages = []

  for (const page of doc.pages ?? []) {
    const key = editKeyFor(page)
    const e = key ? edits[key] : null
    if (!e) { pages.push(page); continue }
    applied.add(key)
    if (e.hidden) continue                       // dropped from the document entirely

    const next = { ...page }

    // Captions: only the slots the operator actually set. An empty string is a
    // MEANINGFUL override — "remove the caption the draft guessed" — so the test
    // is presence of the key, not truthiness of the value.
    if (e.captions && Object.keys(e.captions).length) {
      next.captions = { ...page.captions }
      for (const slot of CAPTION_SLOTS) {
        if (Object.hasOwn(e.captions, slot)) next.captions[slot] = e.captions[slot]
      }
    }

    // Step narrative. `pageDraft` builds the four-panel step layout for pp. 5-6
    // and leaves every caption empty — the movements it describes ("moved to the
    // patient's left by 3.85mm") are the planner's decisions, not anything
    // readable off a mesh. This is where the operator supplies them.
    //
    // Keyed by INDEX, and an empty string is meaningful — "this step has no
    // narrative" — so presence of the key decides, exactly as captions do. The
    // step array is copied rather than mutated: the drafted document is rebuilt
    // from the files on every change and must never be written through.
    if (e.steps && Object.keys(e.steps).length && Array.isArray(next.steps)) {
      next.steps = next.steps.map((st, i) => (
        Object.hasOwn(e.steps, String(i)) ? { ...st, text: e.steps[String(i)] } : st
      ))
    }

    // Page fields — the source-data case summary, the measurement notes. The
    // draft cannot know them (FIBULA p2's "The left fibula has been utilized…"
    // is the planner's sentence), so the operator types them here. Presence of
    // the key decides, as with captions: an empty string clears a drafted one.
    if (e.fields && Object.keys(e.fields).length) {
      for (const f of PAGE_FIELDS) {
        if (Object.hasOwn(e.fields, f)) next[f] = e.fields[f]
      }
    }

    // Annotations are ADDITIVE. The draft never emits any today, but if it
    // starts to, an operator's arrow must not silently delete the draft's own.
    //
    // An annotation may target the PAGE or one panel within it. That matters
    // more than it looks: layout.js gives each cell, step, row and gallery item
    // its own 0..1 frame, so "half way across this panel" is x = 0.5 against the
    // panel — whereas a page-level annotation has to be placed by working out
    // where that panel happens to sit on the sheet. Targeting is what makes the
    // coordinates mean something an operator can predict.
    for (const a of e.annotations ?? []) {
      const t = a.target
      const { target, ...body } = a
      if (!t || t.kind === 'page') {
        next.annotations = [...(next.annotations ?? page.annotations ?? []), body]
        continue
      }
      const listKey = { cell: 'cells', step: 'steps', row: 'rows', item: 'items' }[t.kind]
      const list = listKey && next[listKey]
      // A target that no longer exists (the page changed shape) must not vanish
      // silently — fall back to the page so the operator SEES it in the wrong
      // place rather than wondering where it went.
      if (!Array.isArray(list) || !list[t.index]) {
        next.annotations = [...(next.annotations ?? page.annotations ?? []), body]
        continue
      }
      const copy = [...list]
      copy[t.index] = {
        ...copy[t.index],
        annotations: [...(copy[t.index].annotations ?? []), body],
      }
      next[listKey] = copy
    }

    pages.push(next)
  }

  const orphans = Object.keys(edits).filter((k) => !applied.has(k))
  return { doc: { ...doc, pages }, applied: [...applied], orphans }
}

/**
 * Build an annotation from the fields a UI collects, filling the geometry each
 * kind needs. Coordinates are 0..1 against the page.
 *
 * Throws on an unknown kind rather than returning something undrawable — the
 * writer would silently skip it and the operator would see their arrow simply
 * not appear.
 */
export function makeAnnotation(id, kind, f = {}) {
  if (!ANNOTATION_KINDS.includes(kind)) {
    throw new Error(`makeAnnotation: "${kind}" is not a kind this build can draw `
      + `(${ANNOTATION_KINDS.join(', ')})`)
  }
  const n = (v, d) => (Number.isFinite(+v) ? +v : d)
  // Carried through applyEdits, which routes the annotation to that panel's own
  // 0..1 frame. Absent → the whole page.
  const target = f.target ? { kind: f.target.kind, index: +f.target.index || 0 } : null
  const withTarget = (a) => (target ? { ...a, target } : a)
  switch (kind) {
    case 'text':
      return withTarget(newAnnotation(id, 'text', {
        x: n(f.x, 0.5), y: n(f.y, 0.5), text: String(f.text ?? ''),
        size: n(f.size, 10), weight: f.weight ?? 'normal', align: f.align ?? 'left',
      }))
    case 'arrow':
    case 'dimension':
      return withTarget(newAnnotation(id, kind, {
        x1: n(f.x1, 0.4), y1: n(f.y1, 0.5), x2: n(f.x2, 0.6), y2: n(f.y2, 0.5),
        text: String(f.text ?? ''), size: n(f.size, 10),
      }))
    case 'leader':
    case 'line':
      return withTarget(newAnnotation(id, kind, {
        x1: n(f.x1, 0.4), y1: n(f.y1, 0.4), x2: n(f.x2, 0.6), y2: n(f.y2, 0.6),
        width: n(f.width, 0.4),
      }))
    // `r` — ONE radius, so a circle is actually round. rx/ry normalise against
    // the frame's width and height separately, so equal values give an oval on
    // any non-square frame. layout.js honours rx/ry still, for a real ellipse.
    case 'circle':
      return withTarget(newAnnotation(id, 'circle', {
        cx: n(f.cx, 0.5), cy: n(f.cy, 0.5), r: n(f.r, 0.06), width: n(f.width, 0.4),
      }))
    default:
      throw new Error(`makeAnnotation: unhandled kind "${kind}"`)
  }
}
